import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { employeeService, attendanceService } from '@/services/api'
import {
  ArrowLeft, Phone, Building2, Briefcase, Clock,
  TrendingDown, MonitorUp, CalendarCheck, AlertTriangle,
  UserX, Timer, ShieldCheck, Zap, Signal, Fingerprint, MapPin,
  Activity, LogIn, LogOut
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell
} from 'recharts'
import { format, parseISO, startOfMonth } from 'date-fns'
import { ru } from 'date-fns/locale'
import PeriodFilter from '@/components/analytics/PeriodFilter'
import AttendanceHeatmap from '@/components/analytics/AttendanceHeatmap'
import SyncToTerminalModal from '@/components/common/SyncToTerminalModal'
import clsx from 'clsx'

// --- Конфигурация статусов SENTINEL ---
const STATUS_CONFIG = {
  on_time: { label: 'CLEARED', bg: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  late: { label: 'DELAYED', bg: 'bg-amber-50 text-amber-600 border-amber-100' },
  very_late: { label: 'CRITICAL', bg: 'bg-rose-50 text-rose-600 border-rose-100' },
  absent: { label: 'MISSING', bg: 'bg-slate-100 text-slate-500 border-slate-200' },
  no_schedule: { label: 'NO_PLAN', bg: 'bg-slate-50 text-slate-400 border-slate-200' },
}

// --- Компонент KPI в стиле Sentinel ---
function KPICard({ label, value, sub, icon: Icon, colorClass }) {
  const themes = {
    indigo: "text-indigo-600 bg-indigo-50 border-indigo-100",
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-100",
    amber: "text-amber-600 bg-amber-50 border-amber-100",
    rose: "text-rose-600 bg-rose-50 border-rose-100",
  }
  return (
    <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-2.5 rounded-xl border ${themes[colorClass]}`}>
          <Icon size={20} strokeWidth={2.5} />
        </div>
        {sub && <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">{sub}</span>}
      </div>
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-1">{label}</p>
      <h4 className="text-3xl font-black text-slate-900 tracking-tighter">{value ?? 0}</h4>
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 shadow-xl rounded-2xl p-4">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-50 pb-2">{label}</p>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-[11px] font-bold text-slate-600">{entry.name === 'worked' ? 'Отработано' : 'Переработка'}</span>
              </div>
              <span className="text-[11px] font-black text-slate-900">{entry.value} ч</span>
            </div>
          ))}
        </div>
      </div>
    )
  }
  return null
}

export default function EmployeeProfilePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const today = new Date()
  const [period, setPeriod] = useState({
    date_from: format(startOfMonth(today), 'yyyy-MM-dd'),
    date_to: format(today, 'yyyy-MM-dd'),
  })
  const [syncModalOpen, setSyncModalOpen] = useState(false)

  const { data: employee, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => employeeService.get(id).then((r) => r.data),
  })

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['employee-stats', id, period],
    queryFn: () => employeeService.stats(id, period).then((r) => r.data),
    enabled: !!id,
  })

  const { data: logsData } = useQuery({
    queryKey: ['employee-logs', id, period],
    queryFn: () => attendanceService.logs({ person_type: 'employee', person_id: id, ...period }).then((r) => r.data),
    enabled: !!id,
  })

  if (isLoading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="h-10 w-10 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  )

  const daily = stats?.daily || []
  const chartData = daily.filter(d => d.status !== 'absent').map(d => ({
    label: format(parseISO(d.date), 'dd.MM'),
    worked: +(d.worked_minutes / 60).toFixed(1),
    overtime: +(d.overtime_minutes / 60).toFixed(1),
  }))

  return (
    <div className="min-h-screen bg-[#F8F9FB] p-6 lg:p-12 text-slate-900 font-sans selection:bg-indigo-100">

      {/* HEADER: NAVIGATION & ACTIONS */}
      <header className="max-w-[1600px] mx-auto mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate(-1)}
            className="w-12 h-12 flex items-center justify-center bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-indigo-600 transition-all shadow-sm"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 bg-indigo-600 rounded-md flex items-center justify-center shadow-md">
                <ShieldCheck className="text-white w-3 h-3" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-600">Employee Protocol</span>
            </div>
            <h1 className="text-3xl font-black tracking-tighter text-slate-900 leading-none">Личное дело.</h1>
          </div>
        </div>

        <button
          onClick={() => setSyncModalOpen(true)}
          className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 hover:bg-slate-800 transition-all"
        >
          <MonitorUp size={16} /> Синхронизировать
        </button>
      </header>

      <div className="max-w-[1600px] mx-auto space-y-8">

        {/* HERO SECTION: PROFILE IDENTITY */}
        <section className="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <Fingerprint size={160} />
          </div>

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
            <div className="relative shrink-0">
              <div className="w-40 h-40 rounded-[32px] overflow-hidden border-4 border-slate-50 bg-slate-50 shadow-inner">
                {employee?.photo ? (
                  <img src={employee.photo} className="w-full h-full object-cover" alt={employee.full_name} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl font-black text-slate-300">
                    {employee?.full_name[0]}
                  </div>
                )}
              </div>
              <div className={clsx(
                "absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border shadow-lg",
                employee?.status === 'active' ? 'bg-emerald-500 text-white border-emerald-400' : 'bg-rose-500 text-white border-rose-400'
              )}>
                {employee?.status === 'active' ? 'PROTOCOL_ACTIVE' : 'PROTOCOL_ARCHIVE'}
              </div>
            </div>

            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl md:text-4xl font-black text-slate-900 tracking-tighter mb-4 md:mb-6">{employee?.full_name}</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: Building2, text: employee?.department_name, color: 'text-indigo-600' },
                  { icon: Briefcase, text: employee?.position_name, color: 'text-violet-600' },
                  { icon: Phone, text: employee?.phone, color: 'text-emerald-600' },
                  { icon: Clock, text: `${employee?.work_start} — ${employee?.work_end}`, color: 'text-amber-600' }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-2xl p-4">
                    <item.icon size={16} className={item.color} strokeWidth={2.5} />
                    <span className="text-[11px] font-bold text-slate-600 truncate">{item.text || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* KPI & FILTER */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em]">Основные показатели</h3>
          <div className="bg-white p-1 rounded-2xl border border-slate-200 shadow-sm flex items-center">
            <PeriodFilter onChange={setPeriod} />
          </div>
        </div>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard label="Отработано часов" value={stats?.total_worked_hours} icon={Timer} colorClass="indigo" />
          <KPICard label="Явок вовремя" value={stats?.on_time} sub="Protocol Cleared" icon={CalendarCheck} colorClass="emerald" />
          <KPICard label="Сверхурочно (ч)" value={stats?.total_overtime_hours} icon={Activity} colorClass="amber" />
          <KPICard label="Пропуски" value={stats?.absent} icon={UserX} colorClass="rose" />
        </section>

        {/* ANALYTICS ROW */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Bar Chart */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-[32px] p-5 md:p-8 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-10">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-50 rounded-xl border border-indigo-100 text-indigo-600">
                  <Activity size={20} />
                </div>
                <div>
                  <h4 className="text-lg font-black text-slate-900 tracking-tight">Рабочий цикл</h4>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Worked Hours vs Overtime</p>
                </div>
              </div>
              <div className="flex gap-4 text-[10px] font-black uppercase tracking-widest">
                <div className="flex items-center gap-1.5 text-indigo-600"><div className="w-2 h-2 rounded-full bg-indigo-600" /> Норма</div>
                <div className="flex items-center gap-1.5 text-emerald-500"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Переработка</div>
              </div>
            </div>

            <div className="h-48 md:h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }} dx={-10} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc', radius: 12 }} />
                  <ReferenceLine y={8} stroke="#e2e8f0" strokeDasharray="8 8" />
                  <Bar dataKey="worked" stackId="a" fill="#4f46e5" radius={[0, 0, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={entry.worked >= 8 ? '#4f46e5' : '#818cf8'} />
                    ))}
                  </Bar>
                  <Bar dataKey="overtime" stackId="a" fill="#10b981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Sidebar Analytics */}
          <div className="space-y-8">
            <div className="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-indigo-600">
                <Clock size={28} strokeWidth={2.2} />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Ср. время прихода</p>
                <p className="text-3xl font-black text-slate-900 tracking-tighter">{stats?.avg_arrival || '--:--'}</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm flex-1">
              <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-6">Тепловая карта явок</h4>
              <div className="flex justify-center">
                <AttendanceHeatmap daily={daily} dateFrom={period.date_from} dateTo={period.date_to} />
              </div>
            </div>
          </div>
        </div>

        {/* LOGS TABLE */}
        <section className="bg-white border border-slate-200 rounded-[32px] overflow-hidden shadow-sm">
          <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
            <h3 className="text-lg font-black text-slate-900 tracking-tight leading-none">Детализация протоколов</h3>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sorted by Date Desc</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 border-b border-slate-100">
                  <th className="px-8 py-5">Дата</th>
                  <th className="px-8 py-5 text-center">Протокол</th>
                  <th className="px-8 py-5 text-center">Приход</th>
                  <th className="px-8 py-5 text-center">Уход</th>
                  <th className="px-8 py-5 text-center">Отработано</th>
                  <th className="px-8 py-5 text-right">Статус</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[...daily].reverse().map((d) => {
                  const status = STATUS_CONFIG[d.status] || STATUS_CONFIG.no_schedule
                  return (
                    <tr key={d.date} className="hover:bg-slate-50/30 transition-colors group">
                      <td className="px-8 py-5">
                        <span className="text-sm font-bold text-slate-800 uppercase tracking-tighter">
                          {format(parseISO(d.date), 'dd MMM, EEE', { locale: ru })}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className={clsx("px-3 py-1 rounded-lg text-[10px] font-black tracking-widest border", status.bg)}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className={clsx("font-mono text-xs font-bold", d.check_in ? 'text-indigo-600' : 'text-slate-300')}>
                          {d.check_in || '--:--'}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className={clsx("font-mono text-xs font-bold", d.check_out ? 'text-indigo-600' : 'text-slate-300')}>
                          {d.check_out || '--:--'}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        {d.worked_minutes > 0 ? (
                          <div className="flex flex-col items-center">
                            <span className="text-xs font-black text-slate-900">{Math.floor(d.worked_minutes / 60)}ч {d.worked_minutes % 60}м</span>
                            {d.overtime_minutes > 0 && <span className="text-[9px] font-bold text-emerald-500">+{d.overtime_minutes}m OT</span>}
                          </div>
                        ) : <span className="text-slate-200">—</span>}
                      </td>
                      <td className="px-8 py-5 text-right">
                        <div className="flex items-center justify-end gap-2 text-slate-400">
                          <MapPin size={12} />
                          <span className="text-[10px] font-bold uppercase">Terminal_01</span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* PASSAGES TABLE */}
        <section className="bg-white border border-slate-200 rounded-[32px] overflow-hidden shadow-sm">
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
          <h3 className="text-lg font-black text-slate-900 tracking-tight leading-none">Проходы</h3>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {logsData?.results?.length ?? logsData?.count ?? 0} событий
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 border-b border-slate-100">
                <th className="px-8 py-5">Дата и время</th>
                <th className="px-8 py-5 text-center">Событие</th>
                <th className="px-8 py-5 text-center">Терминал</th>
                <th className="px-8 py-5 text-center">Статус</th>
                <th className="px-8 py-5 text-right">Опоздание</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(logsData?.results ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-8 py-10 text-center text-sm text-slate-400">Нет данных за выбранный период</td>
                </tr>
              )}
              {(logsData?.results ?? []).map((log) => (
                <tr key={log.id} className="hover:bg-slate-50/30 transition-colors">
                  <td className="px-8 py-4">
                    <span className="text-sm font-bold text-slate-800 uppercase tracking-tighter">
                      {format(parseISO(log.timestamp), 'dd MMM yyyy, HH:mm:ss', { locale: ru })}
                    </span>
                  </td>
                  <td className="px-8 py-4 text-center">
                    {log.event_type === 'in' ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black tracking-widest border bg-emerald-50 text-emerald-600 border-emerald-100">
                        <LogIn size={11} /> ВХОД
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black tracking-widest border bg-slate-100 text-slate-500 border-slate-200">
                        <LogOut size={11} /> ВЫХОД
                      </span>
                    )}
                  </td>
                  <td className="px-8 py-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-500">
                      <MapPin size={12} />
                      <span className="text-[11px] font-bold uppercase">{log.device_name ?? 'N/A'}</span>
                    </div>
                  </td>
                  <td className="px-8 py-4 text-center">
                    {log.attendance_status ? (
                      <span className={`px-3 py-1 rounded-lg text-[10px] font-black tracking-widest border ${STATUS_CONFIG[log.attendance_status]?.bg ?? 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                        {STATUS_CONFIG[log.attendance_status]?.label ?? log.attendance_status}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-8 py-4 text-right">
                    {log.late_minutes > 0
                      ? <span className="font-mono text-xs font-bold text-rose-500">+{log.late_minutes}м</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </section>
      </div>

      <SyncToTerminalModal open={syncModalOpen} onClose={() => setSyncModalOpen(false)} personType="employee" personIds={[id]} />
    </div>
  )
}